/**
 * "Till app" in the tablet's sheet, only when the till is running inside the
 * app (apps/till-app, native.ts): which version, which address it opens, and
 * whether the tablet is locked to the till.
 *
 * Anybody may lock the tablet to the till. Leaving the lock, changing the
 * address and releasing a dedicated till are a manager's: the same people who
 * set up the tablet's card reader, so `payments.card_reader.manage`.
 */

import { useEffect, useState } from 'react';
import { usePermission } from '@restaurant/api-client';
import { Button, ErrorNotice, Sheet } from '@restaurant/ui';

import { tillApp, TillAppError, type TillAppInfo } from './native';

export function TillAppRow() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<TillAppInfo | null>(null);

  useEffect(() => {
    void tillApp
      .info()
      .then(setInfo)
      .catch(() => undefined);
  }, [open]);

  return (
    <>
      <ul className="os__more pos__shift-rows">
        <li>
          <button type="button" className="os__more-item" onClick={() => setOpen(true)}>
            <span className="os__more-label">Till app</span>
            <span className="os__more-hint">
              {info
                ? `${info.locked ? 'Locked to the till' : 'Not locked'} · ${info.version}`
                : ''}
            </span>
          </button>
        </li>
      </ul>
      {open && info && (
        <TillAppSheet info={info} onChange={setInfo} onDismiss={() => setOpen(false)} />
      )}
    </>
  );
}

function TillAppSheet({
  info,
  onChange,
  onDismiss,
}: {
  info: TillAppInfo;
  onChange: (info: TillAppInfo) => void;
  onDismiss: () => void;
}) {
  const mayManage = usePermission('payments.card_reader.manage');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);

  // Android's own prompt, or a swipe out of pinning, changes the lock
  // without the till hearing: read it again while the sheet is open.
  useEffect(() => {
    const timer = window.setInterval(() => {
      void tillApp
        .info()
        .then(onChange)
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [onChange]);

  async function act(step: () => Promise<TillAppInfo | null>) {
    setBusy(true);
    setError(null);
    try {
      const next = await step();
      if (next) onChange(next);
    } catch (e) {
      setError(e instanceof TillAppError ? e.message : 'The till app could not do that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Till app" onDismiss={onDismiss}>
      <dl className="ta__facts">
        <div>
          <dt>Version</dt>
          <dd>{info.version}</dd>
        </div>
        <div>
          <dt>Opens</dt>
          <dd>{info.server.replace(/^https:\/\//, '')}</dd>
        </div>
        <div>
          <dt>This tablet</dt>
          <dd>
            {info.dedicated
              ? 'A dedicated till: it locks itself to the till and opens when switched on.'
              : 'An ordinary tablet: Android pins the till when it is locked, and staff can unpin it with the tablet’s own PIN.'}
          </dd>
        </div>
      </dl>

      {!info.locked && (
        <Button variant="primary" loading={busy} onClick={() => void act(tillApp.lock)}>
          Lock to the till
        </Button>
      )}
      {info.locked && mayManage && (
        <>
          <Button loading={busy} onClick={() => void act(tillApp.unlock)}>
            Leave the lock
          </Button>
          <p className="pr__note">
            Until somebody locks it again, or the app next starts, so it is never left open by
            mistake.
          </p>
        </>
      )}

      {mayManage && (
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() =>
            void act(async () => {
              await tillApp.changeServer();
              return null;
            })
          }
        >
          Change the till’s address
        </Button>
      )}

      {!info.dedicated && (
        <p className="pr__note">
          For a lock staff cannot leave, and a till that opens when the tablet is switched on,
          set the tablet up as a dedicated till once. The setup guide that came with the app
          shows how.
        </p>
      )}

      {info.dedicated &&
        mayManage &&
        (releasing ? (
          <>
            <p className="pr__note">
              The tablet stops being a till: no lock, no opening on its own, and the app can be
              uninstalled. Setting it up as a till again usually means resetting it to factory
              settings.
            </p>
            <Button variant="danger" loading={busy} onClick={() => void act(tillApp.release)}>
              Release this tablet
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setReleasing(false)}>
              Keep it a till
            </Button>
          </>
        ) : (
          <Button variant="ghost" disabled={busy} onClick={() => setReleasing(true)}>
            Stop being a dedicated till…
          </Button>
        ))}

      {error && <ErrorNotice title="That did not work" message={error} />}
    </Sheet>
  );
}
